/**
 * Os filtros da Inbox, guardados entre sessões (H07).
 *
 * ── Por que persistir ─────────────────────────────────────────────────────
 *
 * A Técnica da task: *"cada vendedor tem sua rotina, e reconfigurar toda manhã
 * é atrito diário"*. Quem só atende WhatsApp de alta prioridade configura isso
 * uma vez, não trinta vezes por mês.
 *
 * ── Por que a BUSCA fica de fora ──────────────────────────────────────────
 *
 * Ela é a única que carrega dado de pessoa. Quem procura "Marina Albuquerque"
 * deixaria o nome de uma cliente no armazenamento do navegador, legível por
 * qualquer script da página e sobrevivendo à sessão — sem nunca ter pedido isso.
 *
 * E não custa nada abrir mão: busca é momentânea por natureza. Ninguém quer
 * reabrir o produto amanhã com a busca de ontem ainda aplicada; isso pareceria
 * defeito.
 *
 * ── Por chave de usuário, não global ──────────────────────────────────────
 *
 * Duas contas no mesmo navegador — o vendedor e o gestor testando algo — teriam
 * os filtros de uma aparecendo para a outra. A chave carrega revenda e usuário.
 */

export interface FiltrosDaInbox {
  readonly busca: string;
  readonly caixa: string;
  readonly situacao: string;
  readonly responsavel: string;
  readonly canal: string;
  readonly prioridade: string;
}

export const FILTROS_VAZIOS: FiltrosDaInbox = {
  busca: '',
  caixa: '',
  situacao: '',
  responsavel: '',
  canal: '',
  prioridade: '',
};

/** O que é guardado — tudo menos a busca. Ver o cabeçalho. */
const CAMPOS_PERSISTIDOS = ['caixa', 'situacao', 'responsavel', 'canal', 'prioridade'] as const;

export function temFiltroAtivo(filtros: FiltrosDaInbox): boolean {
  return Object.values(filtros).some((valor) => valor !== '');
}

/** Só o que tem valor vira parâmetro: chave vazia é diferente de não filtrar. */
export function comoConsulta(filtros: FiltrosDaInbox): Record<string, string> {
  return Object.fromEntries(Object.entries(filtros).filter(([, valor]) => valor !== ''));
}

export function chaveDeArmazenamento(tenantSlug: string, usuarioId: string): string {
  return `cliqcrm.inbox.filtros:${tenantSlug}:${usuarioId}`;
}

/**
 * Lê o que foi guardado, descartando o que não reconhece.
 *
 * Valor desconhecido no armazenamento não é hipótese remota: é o que sobra
 * depois de uma versão renomear um filtro. Aplicá-lo mandaria `?situacao=antigo`
 * para o servidor, que responderia 400 — e a Inbox abriria em erro, sem o
 * usuário ter feito nada.
 */
export function lerFiltros(
  bruto: string | null,
  valoresAceitos: Readonly<Record<string, readonly string[]>> = {},
): FiltrosDaInbox {
  if (!bruto) return FILTROS_VAZIOS;

  let guardado: Record<string, unknown>;
  try {
    const analisado: unknown = JSON.parse(bruto);
    if (typeof analisado !== 'object' || analisado === null) return FILTROS_VAZIOS;
    guardado = analisado as Record<string, unknown>;
  } catch {
    return FILTROS_VAZIOS;
  }

  const filtros: Record<string, string> = { ...FILTROS_VAZIOS };

  for (const campo of CAMPOS_PERSISTIDOS) {
    const valor = guardado[campo];
    if (typeof valor !== 'string' || valor === '') continue;

    // Lista de valores aceitos quando quem chama souber quais são. Para `caixa`
    // e `responsavel` ela não existe — são ids que variam por revenda.
    const aceitos = valoresAceitos[campo];
    if (aceitos && !aceitos.includes(valor)) continue;

    filtros[campo] = valor;
  }

  return filtros as unknown as FiltrosDaInbox;
}

/** O que vai para o armazenamento — a busca não entra. */
export function escreverFiltros(filtros: FiltrosDaInbox): string {
  return JSON.stringify(
    Object.fromEntries(CAMPOS_PERSISTIDOS.map((campo) => [campo, filtros[campo]])),
  );
}

/**
 * As opções de um filtro, com o valor escolhido garantido dentro.
 *
 * ── A armadilha que isto desarma ──────────────────────────────────────────
 *
 * As opções de "Caixa" vêm do que a lista carregou. Então um filtro que zera a
 * lista zera as opções — e o seletor sumia da tela levando junto o único
 * controle capaz de desfazê-lo.
 *
 * Foi assim que uma fila ficou vazia e presa: `caixa=2` combinado com
 * `canal=whatsapp_cloud`, uma combinação impossível (a caixa 2 é de outro
 * canal). Zero resultados, zero opções, zero pistas do que estava filtrando.
 *
 * Aqui o valor escolhido entra na lista mesmo sem catálogo, mostrando o valor
 * cru. Ver "Caixa: 2" é feio; não ver nada com a fila vazia é pior.
 */
export function opcoesComValor(
  opcoes: readonly (readonly [string, string])[],
  valor: string,
): readonly (readonly [string, string])[] {
  if (valor === '' || opcoes.some(([chave]) => chave === valor)) return opcoes;
  return [...opcoes, [valor, valor] as const];
}

/** O seletor só some quando não há opção NEM valor escolhido. */
export function seletorVisivel(
  opcoes: readonly (readonly [string, string])[],
  valor: string,
): boolean {
  return opcoes.length > 0 || valor !== '';
}
